import { useMemo, useState } from "react";
import { ActionIcon, Button, Card, Center, Group, Loader, Popover, Stack, Text, Title } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { bookingChainKey, groupEvents, TimelineEventList } from "@nodus/ui";
import { isoDay, shiftDay, toTimelineEventView, type MemberEvent } from "@/lib/activity";

export interface ActivityViewProps {
  /** `YYYY-MM-DD` being shown. */
  day: string;
  /** `YYYY-MM-DD` of today (passed in so the view stays pure). */
  today: string;
  onDayChange: (day: string) => void;
  events: MemberEvent[];
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

/** The picker button's label: the day itself (the Today button covers "today"). */
const dayLabel = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** Prev / Today / Next + a date picker — the admin Timeline's day controls,
 *  minus live mode and filters. Never steps into the future. */
export function ActivityDayNav({ day, today, onDayChange }: Pick<ActivityViewProps, "day" | "today" | "onDayChange">) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const atToday = day >= today;
  return (
    <Group gap={6} wrap="nowrap">
      <ActionIcon variant="default" size={32} onClick={() => onDayChange(shiftDay(day, -1))} aria-label="Previous day">
        <ChevronLeft size={16} />
      </ActionIcon>
      <Button variant="default" size="compact-sm" h={32} onClick={() => onDayChange(today)} disabled={atToday}>
        Today
      </Button>
      <ActionIcon
        variant="default"
        size={32}
        onClick={() => onDayChange(shiftDay(day, 1))}
        disabled={atToday}
        aria-label="Next day"
      >
        <ChevronRight size={16} />
      </ActionIcon>
      <Popover opened={calendarOpen} onChange={setCalendarOpen} position="bottom-end">
        <Popover.Target>
          <Button
            variant="default"
            size="compact-sm"
            h={32}
            fw={400}
            leftSection={<CalendarDays size={14} />}
            onClick={() => setCalendarOpen((o) => !o)}
            aria-label="Pick a day"
          >
            {dayLabel(day)}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <DatePicker
            value={day}
            maxDate={today}
            onChange={(value) => {
              if (!value) return;
              // Mantine 9 emits `YYYY-MM-DD`; older callers may hand a Date.
              const next = typeof value === "string" ? value.slice(0, 10) : isoDay(value as Date);
              onDayChange(next > today ? today : next);
              setCalendarOpen(false);
            }}
          />
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}

/** The Activity page body — presentational, so it renders without the API. */
export function ActivityView(props: ActivityViewProps) {
  const { day, today, onDayChange } = props;
  return (
    <Stack p="md" gap="lg" maw={900} mx="auto" w="100%">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
        <Stack gap={4}>
          <Title order={2}>Activity</Title>
          <Text fz="sm" c="dimmed">
            Alarm activity on the floors you have access to, and your company&apos;s own events.
          </Text>
        </Stack>
        <ActivityDayNav day={day} today={today} onDayChange={onDayChange} />
      </Group>
      <ActivityBody {...props} />
    </Stack>
  );
}

export function ActivityBody({
  day,
  today,
  events,
  isLoading,
  error,
  onRetry,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: ActivityViewProps) {
  const items = useMemo(
    () => groupEvents(events.map(toTimelineEventView), { getChainKey: bookingChainKey }),
    [events],
  );

  // Nothing yet but the backend says "ask again": still loading, not empty.
  if (isLoading || (events.length === 0 && hasMore && !error)) {
    return (
      <Center py="xl">
        <Group gap="sm">
          <Loader size="sm" />
          <Text fz="sm" c="dimmed">Loading activity…</Text>
        </Group>
      </Center>
    );
  }
  if (error && events.length === 0) {
    return (
      <Card withBorder padding="lg">
        <Stack gap="sm" align="flex-start">
          <Text fz="sm" c="red">Activity could not be loaded. Please try again.</Text>
          <Button variant="light" size="xs" onClick={onRetry}>Try again</Button>
        </Stack>
      </Card>
    );
  }
  if (events.length === 0) {
    return (
      <Card withBorder padding="lg">
        <Text fz="sm" c="dimmed" ta="center">
          {day === today
            ? "Nothing has happened on the floors you have access to today."
            : "Nothing happened on the floors you have access to on this day."}
        </Text>
      </Card>
    );
  }
  return (
    <Stack gap="md">
      <TimelineEventList items={items} />
      {hasMore && (
        <Center>
          <Button variant="default" onClick={onLoadMore} loading={isLoadingMore}>
            Load more
          </Button>
        </Center>
      )}
    </Stack>
  );
}
